import type { WeComSdkSignature } from './api'
import * as ww from '@wecom/jssdk'
import { fetchWeComSdkSignature } from './api'

export type WeComScheduleBoardEntry = 'internal_group_chat_board' | 'other'
export type WeComScheduleBoardErrorCode
  = 'environment_unavailable'
    | 'signature_unavailable'
    | 'sdk_config_failed'
    | 'sdk_config_timeout'
    | 'context_timeout'
    | 'context_failed'
    | 'context_unsupported'
export type WeComScheduleBoardErrorStage = 'environment' | 'signature' | 'sdk-config' | 'context'

export interface WeComScheduleBoardDiagnostic {
  stage: WeComScheduleBoardErrorStage
  code: WeComScheduleBoardErrorCode
  errCode?: number
  errMsg?: string
  message?: string
}

const diagnosticLimit = 180
const sensitiveDiagnosticKey = /corp[_-]?secret|signature|share[_-]?ticket|ticket|nonce[_-]?str|nonce|access[_-]?token|token|url/iu
let registeredUrl: string | undefined

function sanitizeDiagnostic(value: string) {
  return value
    .replace(/(?:https?:)?\/\/[^\s"'<>]+/giu, '[url]')
    .replace(new RegExp(`(["']?)(?:${sensitiveDiagnosticKey.source})\\1\\s*[:=]\\s*["']?[^,"'\\s;}]+["']?`, 'giu'), '[redacted]')
    .replace(/\b[a-f\d]{40}\b/giu, '[redacted]')
    .replace(/\s+/gu, ' ')
    .trim()
    .slice(0, diagnosticLimit)
}

function diagnosticText(value: unknown) {
  if (typeof value === 'string') {
    return sanitizeDiagnostic(value) || undefined
  }
  return undefined
}

function extractDiagnostic(value: unknown) {
  if (value instanceof Error) {
    const error = value as Error & { errCode?: unknown, errMsg?: unknown }
    return {
      errCode: typeof error.errCode === 'number' ? error.errCode : undefined,
      errMsg: diagnosticText(error.errMsg),
      message: diagnosticText(error.message),
    }
  }
  if (typeof value === 'string') {
    return {
      errCode: undefined,
      errMsg: undefined,
      message: diagnosticText(value),
    }
  }
  if (typeof value !== 'object' || value === null) {
    return {}
  }

  const record = value as Record<string, unknown>
  const result = typeof record.result === 'object' && record.result !== null
    ? record.result as Record<string, unknown>
    : record
  return {
    errCode: typeof record.errCode === 'number'
      ? record.errCode
      : typeof result.errCode === 'number' ? result.errCode : undefined,
    errMsg: diagnosticText(record.errMsg ?? result.errMsg),
    message: diagnosticText(record.message ?? (result === record ? undefined : result.message)),
  }
}

function hasUnsupportedHint(value: unknown) {
  const details = extractDiagnostic(value)
  const text = `${details.errMsg ?? ''} ${details.message ?? ''}`.toLowerCase()
  return /unsupported|not\s+support|不支持|版本过低|版本不兼容/iu.test(text)
}

function errorMessage(code: WeComScheduleBoardErrorCode) {
  switch (code) {
    case 'environment_unavailable':
      return '当前页面不在企业微信环境中，请从企业微信内部群群看板进入。'
    case 'signature_unavailable':
      return '企微看板配置服务不可用，请检查网络或稍后重试。'
    case 'sdk_config_failed':
      return '企微 JS-SDK 配置失败，请检查应用可见范围、JS-SDK 安全域名和签名配置。'
    case 'sdk_config_timeout':
      return '企微 JS-SDK 初始化超时，请检查网络、应用配置和企业微信客户端状态。'
    case 'context_timeout':
      return '企微入口检测超时，请确认从内部群群看板进入后重试。'
    case 'context_failed':
      return '企微入口读取失败，请确认从内部群群看板进入且应用权限已配置。'
    case 'context_unsupported':
      return '当前企业微信客户端不支持读取群看板入口，请升级到支持该能力的版本后重试。'
  }
}

export class WeComScheduleBoardError extends Error {
  readonly code: WeComScheduleBoardErrorCode
  readonly stage: WeComScheduleBoardErrorStage
  readonly errCode?: number
  readonly errMsg?: string
  readonly diagnostic: WeComScheduleBoardDiagnostic

  constructor(code: WeComScheduleBoardErrorCode, stage: WeComScheduleBoardErrorStage, cause?: unknown) {
    const details = extractDiagnostic(cause)
    const suffix = details.errMsg ?? details.message
    super(suffix ? `${errorMessage(code)}（诊断：${suffix}）` : errorMessage(code))
    this.name = 'WeComScheduleBoardError'
    this.code = code
    this.stage = stage
    this.errCode = details.errCode
    this.errMsg = details.errMsg
    this.diagnostic = {
      code,
      stage,
      errCode: details.errCode,
      errMsg: details.errMsg,
      message: details.message,
    }
    Object.setPrototypeOf(this, new.target.prototype)
  }
}

export function getWeComScheduleBoardErrorMessage(error: unknown) {
  if (error instanceof WeComScheduleBoardError) {
    const diagnosticParts = [
      error.errCode === undefined ? undefined : `错误码 ${error.errCode}`,
      error.errMsg,
      error.diagnostic.message ? `异常：${error.diagnostic.message}` : undefined,
    ].filter((part): part is string => Boolean(part))
    return diagnosticParts.length > 0
      ? `${errorMessage(error.code)}（诊断：${diagnosticParts.join('；')}）`
      : errorMessage(error.code)
  }
  if (error instanceof Error) {
    return sanitizeDiagnostic(error.message) || '看板数据暂时不可用'
  }
  return '看板数据暂时不可用'
}

class SdkTimeoutError extends Error {
  constructor(message: string) {
    super(message)
    this.name = 'SdkTimeoutError'
    Object.setPrototypeOf(this, new.target.prototype)
  }
}

async function withSdkTimeout<T>(promise: Promise<T>, message: string): Promise<T> {
  let timeout: ReturnType<typeof setTimeout> | undefined
  try {
    return await Promise.race([
      promise,
      new Promise<T>((_, reject) => {
        timeout = setTimeout(() => reject(new SdkTimeoutError(message)), 8_000)
      }),
    ])
  }
  finally {
    if (timeout) {
      clearTimeout(timeout)
    }
  }
}

export async function readWeComScheduleBoardEntry(): Promise<WeComScheduleBoardEntry> {
  if (typeof window === 'undefined') {
    throw new WeComScheduleBoardError('environment_unavailable', 'environment')
  }

  const pageUrl = new URL(window.location.href)
  pageUrl.searchParams.delete('code')
  pageUrl.searchParams.delete('state')
  pageUrl.searchParams.delete('errmsg')
  pageUrl.hash = ''
  if (registeredUrl !== pageUrl.href) {
    let initialSignature: WeComSdkSignature
    try {
      initialSignature = await fetchWeComSdkSignature(pageUrl.href)
    }
    catch (error) {
      throw new WeComScheduleBoardError('signature_unavailable', 'signature', error)
    }

    let firstSignature: WeComSdkSignature | undefined = initialSignature
    let firstAgentConfigSignature: WeComSdkSignature | undefined = initialSignature
    try {
      ww.register({
        agentId: initialSignature.agentId,
        corpId: initialSignature.corpId,
        getAgentConfigSignature: async (url) => {
          const normalizedUrl = new URL(url)
          normalizedUrl.hash = ''
          if (firstAgentConfigSignature && normalizedUrl.href === pageUrl.href) {
            const signature = firstAgentConfigSignature
            firstAgentConfigSignature = undefined
            return {
              nonceStr: signature.agentConfigNonceStr,
              signature: signature.agentConfigSignature,
              timestamp: signature.agentConfigTimestamp,
            }
          }
          const signature = await fetchWeComSdkSignature(normalizedUrl.href)
          return {
            nonceStr: signature.agentConfigNonceStr,
            signature: signature.agentConfigSignature,
            timestamp: signature.agentConfigTimestamp,
          }
        },
        getConfigSignature: async (url) => {
          const normalizedUrl = new URL(url)
          normalizedUrl.hash = ''
          if (firstSignature && normalizedUrl.href === pageUrl.href) {
            const signature = firstSignature
            firstSignature = undefined
            return {
              nonceStr: signature.nonceStr,
              signature: signature.signature,
              timestamp: signature.timestamp,
            }
          }
          const signature = await fetchWeComSdkSignature(normalizedUrl.href)
          return {
            nonceStr: signature.nonceStr,
            signature: signature.signature,
            timestamp: signature.timestamp,
          }
        },
        jsApiList: ['getContext'],
      })
      const config = await withSdkTimeout<Awaited<ReturnType<typeof ww.ensureConfigReady>>>(
        ww.ensureConfigReady(),
        '企微 JS-SDK 初始化超时',
      )
      const configDetails = extractDiagnostic(config.result)
      if (configDetails.errCode !== undefined && configDetails.errCode !== 0) {
        throw new WeComScheduleBoardError('sdk_config_failed', 'sdk-config', config.result)
      }
      registeredUrl = pageUrl.href
    }
    catch (error) {
      if (error instanceof SdkTimeoutError) {
        throw new WeComScheduleBoardError('sdk_config_timeout', 'sdk-config', error)
      }
      if (error instanceof WeComScheduleBoardError) {
        throw error
      }
      throw new WeComScheduleBoardError('sdk_config_failed', 'sdk-config', error)
    }
  }

  let context: Awaited<ReturnType<typeof ww.getContext>>
  try {
    context = await withSdkTimeout(ww.getContext(), '企微入口检测超时')
  }
  catch (error) {
    if (error instanceof SdkTimeoutError) {
      throw new WeComScheduleBoardError('context_timeout', 'context', error)
    }
    const code = hasUnsupportedHint(error) ? 'context_unsupported' : 'context_failed'
    throw new WeComScheduleBoardError(code, 'context', error)
  }
  if (context.errCode !== 0) {
    const code = hasUnsupportedHint(context) ? 'context_unsupported' : 'context_failed'
    throw new WeComScheduleBoardError(code, 'context', context)
  }
  return context.entry === 'internal_group_chat_board' ? 'internal_group_chat_board' : 'other'
}
